import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import AutoSalaryBreakdown from '../../../components/AutoSalaryBreakdown'
import * as api from '../../../lib/api'

function pluralize(n, one, two, five) {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 19) return five
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return two
  return five
}

export default function AllSalariesSummary({
  allSalaries,
  extendedSalaries = [],
  loadingAllSalaries,
  employees,
  profile,
  isAdmin,
  isOwner,
  canManagePayments,
  expandedEmployeeId,
  setExpandedEmployeeId,
  filterPeriodStart,
  filterPeriodEnd,
  filterEmployee,
  showAcceptance,
  showIssued,
  showReturns,
  customColumns,
  gridCols,
  formatMoney,
  formatDate,
  handleOpenPayModal,
  showUpcomingPayouts,
  setShowUpcomingPayouts,
  avgSalaryData,
  toast,
  setRefreshTrigger,
  setShowPayoutsDetailModal,
  setSalaryDetailModal,
  totalPaid = 0
}) {
  if (loadingAllSalaries) {
    return (
      <div className="all-salaries-card card animate-fade-in">
        <div className="all-salaries-header">
          <strong>Сводка ЗП по всем сотрудникам</strong>
        </div>
        <div className="all-salaries-table animate-fade-in" style={{ padding: '20px 16px' }}>
          <div className="skeleton-pulse" style={{ height: '56px', borderRadius: '12px', marginBottom: '10px' }}></div>
          <div className="skeleton-pulse" style={{ height: '56px', borderRadius: '12px', marginBottom: '10px' }}></div>
          <div className="skeleton-pulse" style={{ height: '56px', borderRadius: '12px', marginBottom: '10px' }}></div>
          <div className="skeleton-pulse" style={{ height: '56px', borderRadius: '12px' }}></div>
        </div>
      </div>
    )
  }

  const [showInactive, setShowInactive] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')

  const hasActivity = (s) => {
    return (
      (s.daysWorked || 0) > 0 ||
      (s.totalHours || 0) > 0 ||
      (s.totalAccepted || 0) > 0 ||
      (s.totalIssued || 0) > 0 ||
      (s.totalReturned || 0) > 0 ||
      (s.totalBonuses || 0) > 0 ||
      (s.totalFines || 0) > 0 ||
      (s.totalWbDeductions || 0) > 0 ||
      (s.total || 0) !== 0 ||
      (s.periodDebt || 0) > 0 ||
      (s.periodPaid || 0) > 0
    )
  }

  const filteredBySearch = React.useMemo(() => {
    if (!searchQuery.trim()) return allSalaries
    return allSalaries.filter(s => s.employeeName?.toLowerCase().includes(searchQuery.toLowerCase()))
  }, [allSalaries, searchQuery])

  const activeSalaries = filteredBySearch.filter(hasActivity)
  const inactiveSalaries = filteredBySearch.filter(s => !hasActivity(s))

  const grandTotal = allSalaries.reduce((sum, s) => sum + Math.max(s.total || 0, 0), 0)
  const totalPaidForPeriod = totalPaid

  const getSalaryRowStatus = (s) => {
    const isAnalytics = Boolean(s.isAnalyticsRange)
    const status = s.paymentStatus || (s.periodDebt != null && s.periodDebt <= 0 ? 'paid' : 'unpaid')
    const isDataUnavailable = Boolean(s.financialDataUnavailable || s.status === 'data_unavailable')
    const isRev = !isDataUnavailable && (status === 'review' || Boolean(s.isReviewRequired || s.hasUnresolvedLegacy))
    const isPaid = !isDataUnavailable && !isRev && status === 'paid'
    const isPartial = !isDataUnavailable && !isRev && status === 'partial'
    const hasDebt = !isDataUnavailable && !isAnalytics && s.periodDebt != null && s.periodDebt > 0
    return { isAnalytics, status: isDataUnavailable ? 'data_unavailable' : (isRev ? 'review' : (isPaid ? 'paid' : (isPartial ? 'partial' : 'unpaid'))), isDataUnavailable, isRev, isPaid, isPartial, hasDebt }
  }

  const renderMobileSalaryStatus = (s) => {
    const { isAnalytics, isDataUnavailable, isRev, isPaid, isPartial, hasDebt } = getSalaryRowStatus(s)
    const displayAmount = isAnalytics ? s.total : (hasDebt ? s.periodDebt : s.total)

    return (
      <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {isDataUnavailable ? (
            <span style={{
              fontSize: '11px',
              padding: '2px 6px',
              borderRadius: '4px',
              background: 'rgba(239, 68, 68, 0.12)',
              color: 'var(--danger, #dc2626)',
              fontWeight: 600,
              border: '1px solid rgba(239, 68, 68, 0.25)'
            }} title={s.unresolvedLegacyNotice || 'Не удалось загрузить все данные для расчёта зарплаты'}>
              Данные недоступны
            </span>
          ) : isRev ? (
            <span style={{
              fontSize: '11px',
              padding: '2px 6px',
              borderRadius: '4px',
              background: 'rgba(245, 158, 11, 0.15)',
              color: '#d97706',
              fontWeight: 600,
              border: '1px solid rgba(245, 158, 11, 0.3)'
            }}>
              Требуется сверка
            </span>
          ) : isPaid ? (
            <>
              <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>
                {formatMoney(s.total)}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--success)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Выплачено ✓
              </div>
            </>
          ) : isPartial ? (
            <>
              <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>
                {formatMoney(s.total)}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--warning, #d97706)', fontWeight: 500 }}>
                {s.paymentAmountsExact && s.paymentOutstandingAmount != null
                  ? `Остаток ${formatMoney(s.paymentOutstandingAmount)}`
                  : 'Частично выплачено'}
              </div>
            </>
          ) : (
            <>
              <div style={{
                fontWeight: 700, fontSize: '15px',
                color: 'var(--text-primary)'
              }}>
                {formatMoney(displayAmount)}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
                {isAnalytics ? 'начислено' : (hasDebt && s.total !== s.periodDebt ? `из ${formatMoney(s.total)}` : 'к выплате')}
              </div>
            </>
          )}
        </div>
        {((!isAnalytics && hasDebt) || isAnalytics) && isOwner && (
          !isRev && !isPaid && (
            <button
              className="btn btn-primary"
              style={{ padding: '8px 10px', fontSize: '12px', borderRadius: '6px', height: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={(e) => {
                e.stopPropagation()
                handleOpenPayModal(s)
              }}
              title={isPartial ? "Выплатить остаток" : (isAnalytics ? "Выплатить сотруднику" : "Выплатить зарплату")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            </button>
          )
        )}
      </div>
    )
  }

  const renderDesktopSalaryStatus = (s) => {
    const { isAnalytics, isDataUnavailable, isRev, isPaid, isPartial, hasDebt } = getSalaryRowStatus(s)

    if (isDataUnavailable) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
          <span style={{
            fontSize: '11px',
            padding: '3px 8px',
            borderRadius: '6px',
            background: 'rgba(239, 68, 68, 0.12)',
            color: 'var(--danger, #dc2626)',
            fontWeight: 600,
            border: '1px solid rgba(239, 68, 68, 0.25)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }} title={s.unresolvedLegacyNotice || 'Не удалось загрузить все данные для расчёта зарплаты'}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Данные недоступны
          </span>
        </div>
      )
    }

    if (s.isAnalyticsRange) {
      if (isRev) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
            <span style={{
              fontSize: '11px',
              padding: '3px 8px',
              borderRadius: '6px',
              background: 'rgba(245, 158, 11, 0.15)',
              color: '#d97706',
              fontWeight: 600,
              border: '1px solid rgba(245, 158, 11, 0.3)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }} title="Требуется сверка исторических выплат">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              Требуется сверка
            </span>
          </div>
        )
      }

      if (isPaid) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '14px' }}>
              {formatMoney(s.total)}
            </span>
            <span style={{ fontSize: '11px', color: 'var(--success)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Выплачено ✓
            </span>
          </div>
        )
      }

      return (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '14px' }}>
              {formatMoney(s.total)}
            </span>
            <span style={{ fontSize: '11px', color: isPartial ? 'var(--warning, #d97706)' : 'var(--text-tertiary)' }}>
              {isPartial ? (s.paymentAmountsExact && s.paymentOutstandingAmount != null ? `остаток ${formatMoney(s.paymentOutstandingAmount)}` : 'частично выплачено') : 'начислено'}
            </span>
          </div>
          {isOwner && !isPaid && !isRev && (
            <button
              className="btn btn-primary"
              style={{ padding: '6px 8px', fontSize: '11px', borderRadius: '6px', height: 'auto', minWidth: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={(e) => {
                e.stopPropagation()
                handleOpenPayModal(s)
              }}
              title="Выплатить сотруднику"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            </button>
          )}
        </>
      )
    }

    if (isRev) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
          <span style={{
            fontSize: '11px',
            padding: '3px 8px',
            borderRadius: '6px',
            background: 'rgba(245, 158, 11, 0.15)',
            color: '#d97706',
            fontWeight: 600,
            border: '1px solid rgba(245, 158, 11, 0.3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }} title="Требуется сверка исторических выплат">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Требуется сверка
          </span>
        </div>
      )
    }

    const debt = s.periodDebt
    const paid = s.periodPaid || 0
    if (debt == null) return <span style={{ fontWeight: 600 }}>{formatMoney(s.total)}</span>
    if (debt <= 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <span style={{ fontSize: '12px', color: 'var(--success)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Есть связанные выплаты</span>
        </div>
      )
    }

    return (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '14px' }}>{formatMoney(debt)}</span>
          {paid > 0 ? (
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>выплачено {formatMoney(paid)}</span>
          ) : (
            debt !== s.total && <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>из {formatMoney(s.total)}</span>
          )}
        </div>
        {isOwner && !isRev && !isPaid && (
          <button
            className="btn btn-primary"
            style={{ padding: '6px 8px', fontSize: '11px', borderRadius: '6px', height: 'auto', minWidth: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={(e) => {
              e.stopPropagation()
              handleOpenPayModal(s)
            }}
            title="Выплатить зарплату"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          </button>
        )}
      </>
    )
  }

  return (
    <div className="all-salaries-card card animate-fade-in">
      <div className="all-salaries-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <strong>Сводка ЗП по сотрудникам</strong>
          <span style={{ fontSize: '12px', background: 'var(--wb-primary-ultra-light)', color: 'var(--wb-primary)', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
            {activeSalaries.length} активн.
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              className="input"
              placeholder="Поиск сотрудника..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '6px 12px 6px 30px',
                fontSize: '12px',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                background: 'var(--surface)',
                color: 'var(--text-primary)',
                width: '180px'
              }}
            />
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }}
            >
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5, padding: '2px' }}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* Mobile View */}
        <div className="salary-cards-mobile">
          {activeSalaries.map((s) => {
            const { isPaid, isRev, isPartial, hasDebt } = getSalaryRowStatus(s)
            const borderColor = isRev
              ? 'var(--warning, #f59e0b)'
              : (isPaid
                ? 'var(--success)'
                : (isPartial
                  ? 'var(--warning, #f59e0b)'
                  : 'var(--surface-border)'))

            return (
              <div
                key={s.employeeId}
                className="salary-employee-card"
                onClick={() => setExpandedEmployeeId(expandedEmployeeId === s.employeeId ? null : s.employeeId)}
                style={{
                  cursor: 'pointer',
                  padding: '10px 12px',
                  borderBottom: '1px solid var(--border-color)',
                  borderLeft: `3px solid ${borderColor}`,
                  background: isPaid ? 'rgba(16,185,129,0.03)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                      <span style={{ fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.employeeName}</span>
                      {(s.isLegacy || s.is_legacy) && (
                        <span style={{
                          fontSize: '10px',
                          padding: '1px 6px',
                          borderRadius: '4px',
                          background: 'var(--surface-subtle, rgba(255, 255, 255, 0.06))',
                          color: 'var(--text-tertiary, #9ca3af)',
                          border: '1px solid var(--surface-border, rgba(255, 255, 255, 0.1))',
                          fontWeight: 500,
                          flexShrink: 0
                        }}>
                          Уволен
                        </span>
                      )}
                      <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {s.daysWorked}д · {Number(s.totalHours).toFixed(1).replace(/\.0$/, '')}ч
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span>{formatMoney(s.basePay)}</span>
                      {(s.totalBonuses || 0) > 0 && <span style={{ color: 'var(--success)' }}>+{formatMoney(s.totalBonuses)}</span>}
                      {(s.totalFines || 0) > 0 && <span style={{ color: 'var(--danger)' }}>−{formatMoney(s.totalFines)}</span>}
                      {(s.totalWbDeductions || 0) > 0 && <span style={{ color: 'var(--danger)' }}>WB −{formatMoney(s.totalWbDeductions)}</span>}
                      <span style={{ marginLeft: 'auto', opacity: 0.4, display: 'inline-flex', transform: expandedEmployeeId === s.employeeId ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg></span>
                    </div>
                  </div>
                  {renderMobileSalaryStatus(s)}
                </div>

                <AnimatePresence>
                  {expandedEmployeeId === s.employeeId && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div style={{ paddingTop: '12px', marginTop: '10px', borderTop: '1px solid var(--border-color)' }}>
                        {s.actualPeriod && (
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                            <span>Период: <strong>{formatDate(s.actualPeriod.displayStart || s.actualPeriod.start)} — {formatDate(s.actualPeriod.end)}</strong></span>
                            {s.actualPeriod.payoutLabel && <span style={{ opacity: 0.7 }}>{s.actualPeriod.payoutLabel}</span>}
                          </div>
                        )}
                        <AutoSalaryBreakdown
                          autoSalary={s}
                          filterEmployee={s.employeeId}
                          profile={profile}
                          filterPeriodStart={filterPeriodStart}
                          filterPeriodEnd={filterPeriodEnd}
                          setSalaryDetailModal={setSalaryDetailModal}
                          isAdmin={isAdmin}
                          isOwner={isOwner}
                          onPaySalary={s.paymentStatus === 'paid' ? undefined : () => handleOpenPayModal(s)}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}

          {inactiveSalaries.length > 0 && (
            <div style={{ marginTop: '12px', padding: '0 4px' }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={(e) => { e.stopPropagation(); setShowInactive(!showInactive); }}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'var(--surface-hover)',
                  border: '1px solid var(--surface-border)',
                  borderRadius: '12px',
                  padding: '10px 14px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--text-tertiary)', display: 'inline-block' }}></span>
                  <span>Без операций и смен ({inactiveSalaries.length})</span>
                </div>
                <span style={{ transform: showInactive ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'inline-flex', opacity: 0.6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                </span>
              </button>

              {showInactive && (
                <div style={{ marginTop: '8px', border: '1px dashed var(--border-color)', borderRadius: '10px', overflow: 'hidden' }}>
                  {inactiveSalaries.map((s) => {
                    const { isPaid, isRev, isPartial, hasDebt } = getSalaryRowStatus(s)
                    const borderColor = isRev
                      ? 'var(--warning, #f59e0b)'
                      : (isPaid
                        ? 'var(--success)'
                        : (isPartial
                          ? 'var(--warning, #f59e0b)'
                          : 'var(--surface-border)'))

                    return (
                      <div
                        key={s.employeeId}
                        className="salary-employee-card"
                        onClick={() => setExpandedEmployeeId(expandedEmployeeId === s.employeeId ? null : s.employeeId)}
                        style={{
                          cursor: 'pointer',
                          padding: '10px 12px',
                          borderBottom: '1px solid var(--border-color)',
                          borderLeft: `3px solid ${borderColor}`,
                          background: isPaid ? 'rgba(16,185,129,0.03)' : 'transparent',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                              <span style={{ fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.employeeName}</span>
                              {(s.isLegacy || s.is_legacy) && (
                                <span style={{
                                  fontSize: '10px',
                                  padding: '1px 6px',
                                  borderRadius: '4px',
                                  background: 'var(--surface-subtle, rgba(255, 255, 255, 0.06))',
                                  color: 'var(--text-tertiary, #9ca3af)',
                                  border: '1px solid var(--surface-border, rgba(255, 255, 255, 0.1))',
                                  fontWeight: 500,
                                  flexShrink: 0
                                }}>
                                  Уволен
                                </span>
                              )}
                              <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {s.daysWorked}д · {Number(s.totalHours).toFixed(1).replace(/\.0$/, '')}ч
                              </span>
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <span>{formatMoney(s.basePay)}</span>
                              {(s.totalBonuses || 0) > 0 && <span style={{ color: 'var(--success)' }}>+{formatMoney(s.totalBonuses)}</span>}
                              {(s.totalFines || 0) > 0 && <span style={{ color: 'var(--danger)' }}>−{formatMoney(s.totalFines)}</span>}
                              {(s.totalWbDeductions || 0) > 0 && <span style={{ color: 'var(--danger)' }}>WB −{formatMoney(s.totalWbDeductions)}</span>}
                              <span style={{ marginLeft: 'auto', opacity: 0.4, display: 'inline-flex', transform: expandedEmployeeId === s.employeeId ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg></span>
                            </div>
                          </div>
                          {renderMobileSalaryStatus(s)}
                        </div>
                        <AnimatePresence>
                          {expandedEmployeeId === s.employeeId && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              style={{ overflow: 'hidden' }}
                            >
                              <div style={{ paddingTop: '12px', marginTop: '10px', borderTop: '1px solid var(--border-color)' }}>
                                {s.actualPeriod && (
                                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Период: <strong>{formatDate(s.actualPeriod.displayStart || s.actualPeriod.start)} — {formatDate(s.actualPeriod.end)}</strong></span>
                                    {s.actualPeriod.payoutLabel && <span style={{ opacity: 0.7 }}>{s.actualPeriod.payoutLabel}</span>}
                                  </div>
                                )}
                                <AutoSalaryBreakdown
                                  autoSalary={s}
                                  filterEmployee={s.employeeId}
                                  profile={profile}
                                  filterPeriodStart={filterPeriodStart}
                                  filterPeriodEnd={filterPeriodEnd}
                                  setSalaryDetailModal={setSalaryDetailModal}
                                  isAdmin={isAdmin}
                                  isOwner={isOwner}
                                  onPaySalary={s.paymentStatus === 'paid' ? undefined : () => handleOpenPayModal(s)}
                                />
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Desktop View */}
        <div className="all-salaries-table-wrapper salary-desktop-only" style={{ overflowX: 'auto', margin: '0 -20px', padding: '0 20px' }}>
          <div className="all-salaries-table" style={{ minWidth: '100%' }}>
            <div
              className="all-salaries-row all-salaries-row-header"
              style={{ gridTemplateColumns: gridCols, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)' }}
            >
              <span>Сотрудник</span>
              <span title="Отработано дней">Дни</span>
              <span title="Отработано часов">Часы</span>
              {showAcceptance && <span>Приёмка</span>}
              {showIssued && <span>Выдача</span>}
              {showReturns && <span>Возвраты</span>}
              {customColumns.map(col => (
                <span key={col} title={col} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {col}
                </span>
              ))}
              <span>Премии</span>
              <span>Штрафы</span>
              <span style={{ textAlign: 'right' }}>
                {activeSalaries.some(s => s.isAnalyticsRange) ? 'Начислено' : 'К выплате'}
              </span>
            </div>
            {activeSalaries.map((s) => {
              return (
                <React.Fragment key={s.employeeId}>
                  <div
                    className="all-salaries-row"
                    style={{ gridTemplateColumns: gridCols, cursor: 'pointer', background: expandedEmployeeId === s.employeeId ? 'var(--surface-hover)' : '', borderBottom: '1px solid var(--border-color)', fontSize: '14px' }}
                    onClick={() => setExpandedEmployeeId(expandedEmployeeId === s.employeeId ? null : s.employeeId)}
                  >
                    <span className="all-salaries-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px' }} title={s.employeeName}>
                      <span>{s.employeeName}</span>
                      {(s.isLegacy || s.is_legacy) && (
                        <span style={{
                          fontSize: '10px',
                          padding: '1px 6px',
                          borderRadius: '4px',
                          background: 'var(--surface-subtle, rgba(255, 255, 255, 0.06))',
                          color: 'var(--text-tertiary, #9ca3af)',
                          border: '1px solid var(--surface-border, rgba(255, 255, 255, 0.1))',
                          fontWeight: 500,
                          flexShrink: 0
                        }}>
                          Уволен
                        </span>
                      )}
                    </span>
                    <span style={{ color: 'var(--text-secondary)' }}>{s.daysWorked}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{Number(s.totalHours).toFixed(1).replace(/\.0$/, '')} ч</span>
                    {showAcceptance && (
                      <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', justifyContent: 'center' }}>
                        <span>{s.totalAccepted + (s.totalAcceptedLate || 0)}</span>
                        {(s.totalAcceptedLate || 0) > 0 && <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: 1 }}>({s.totalAccepted} вовр.)</span>}
                      </span>
                    )}
                    {showIssued && <span>{s.totalIssued}</span>}
                    {showReturns && <span>{s.totalReturned || 0}</span>}

                    {customColumns.map(col => {
                      const detail = (s.customWorkDetails || []).find(d => d.name === col)
                      return (
                        <span key={col} style={{ color: detail?.value > 0 ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                          {detail ? `${detail.value}` : '—'}
                        </span>
                      )
                    })}

                    <span style={{ color: (s.totalBonuses || 0) > 0 ? 'var(--success)' : 'var(--text-tertiary)' }}>
                      {(s.totalBonuses || 0) > 0 ? `+${formatMoney(s.totalBonuses)}` : '—'}
                    </span>
                    <span style={{ color: (s.totalFines + (s.totalWbDeductions || 0)) > 0 ? 'var(--danger)' : 'var(--text-tertiary)' }}>
                      {(s.totalFines + (s.totalWbDeductions || 0)) > 0 ? `−${formatMoney(s.totalFines + (s.totalWbDeductions || 0))}` : '—'}
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', textAlign: 'right' }}>
                      {renderDesktopSalaryStatus(s)}
                    </span>
                  </div>
                  <AnimatePresence>
                    {expandedEmployeeId === s.employeeId && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ gridColumn: '1 / -1', overflow: 'hidden' }}
                      >
                        <div style={{ padding: '24px', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
                          {s.actualPeriod && (
                            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed var(--border-color)', paddingBottom: '8px' }}>
                              <span>Период начисления: <strong>{formatDate(s.actualPeriod.displayStart || s.actualPeriod.start)} — {formatDate(s.actualPeriod.end)}</strong></span>
                              {s.actualPeriod.payoutLabel && <span style={{ opacity: 0.8 }}>Ближайшая выплата: <strong>{s.actualPeriod.payoutLabel}</strong></span>}
                            </div>
                          )}
                          <AutoSalaryBreakdown
                            autoSalary={s}
                            filterEmployee={s.employeeId}
                            profile={profile}
                            filterPeriodStart={filterPeriodStart}
                            filterPeriodEnd={filterPeriodEnd}
                            setSalaryDetailModal={setSalaryDetailModal}
                            isAdmin={isAdmin}
                            isOwner={isOwner}
                            onPaySalary={s.paymentStatus === 'paid' ? undefined : () => {
                              handleOpenPayModal(s)
                              return true
                            }}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </React.Fragment>
              )
            })}

            {inactiveSalaries.length > 0 && (
              <div
                style={{
                  gridColumn: '1 / -1',
                  margin: '12px 0 8px 0',
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: 'var(--text-secondary)'
                }}
                onClick={() => setShowInactive(!showInactive)}
              >
                <span>Сотрудники без смен и операций за период ({inactiveSalaries.length})</span>
                <span style={{ transform: showInactive ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'inline-flex' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                </span>
              </div>
            )}

            {showInactive && inactiveSalaries.map((s) => {
              return (
                <React.Fragment key={s.employeeId}>
                  <div
                    className="all-salaries-row"
                    style={{ gridTemplateColumns: gridCols, cursor: 'pointer', background: expandedEmployeeId === s.employeeId ? 'var(--surface-hover)' : '', borderBottom: '1px solid var(--border-color)', fontSize: '14px' }}
                    onClick={() => setExpandedEmployeeId(expandedEmployeeId === s.employeeId ? null : s.employeeId)}
                  >
                    <span className="all-salaries-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px' }} title={s.employeeName}>
                      <span>{s.employeeName}</span>
                      {(s.isLegacy || s.is_legacy) && (
                        <span style={{
                          fontSize: '10px',
                          padding: '1px 6px',
                          borderRadius: '4px',
                          background: 'var(--surface-subtle, rgba(255, 255, 255, 0.06))',
                          color: 'var(--text-tertiary, #9ca3af)',
                          border: '1px solid var(--surface-border, rgba(255, 255, 255, 0.1))',
                          fontWeight: 500,
                          flexShrink: 0
                        }}>
                          Уволен
                        </span>
                      )}
                    </span>
                    <span style={{ color: 'var(--text-secondary)' }}>{s.daysWorked}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{Number(s.totalHours).toFixed(1).replace(/\.0$/, '')} ч</span>
                    {showAcceptance && (
                      <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', justifyContent: 'center' }}>
                        <span>{s.totalAccepted + (s.totalAcceptedLate || 0)}</span>
                        {(s.totalAcceptedLate || 0) > 0 && <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: 1 }}>({s.totalAccepted} вовр.)</span>}
                      </span>
                    )}
                    {showIssued && <span>{s.totalIssued}</span>}
                    {showReturns && <span>{s.totalReturned || 0}</span>}

                    {customColumns.map(col => {
                      const detail = (s.customWorkDetails || []).find(d => d.name === col)
                      return (
                        <span key={col} style={{ color: detail?.value > 0 ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                          {detail ? `${detail.value}` : '—'}
                        </span>
                      )
                    })}

                    <span style={{ color: (s.totalBonuses || 0) > 0 ? 'var(--success)' : 'var(--text-tertiary)' }}>
                      {(s.totalBonuses || 0) > 0 ? `+${formatMoney(s.totalBonuses)}` : '—'}
                    </span>
                    <span style={{ color: (s.totalFines + (s.totalWbDeductions || 0)) > 0 ? 'var(--danger)' : 'var(--text-tertiary)' }}>
                      {(s.totalFines + (s.totalWbDeductions || 0)) > 0 ? `−${formatMoney(s.totalFines + (s.totalWbDeductions || 0))}` : '—'}
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', textAlign: 'right' }}>
                      {renderDesktopSalaryStatus(s)}
                    </span>
                  </div>
                  <AnimatePresence>
                    {expandedEmployeeId === s.employeeId && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ gridColumn: '1 / -1', overflow: 'hidden' }}
                      >
                        <div style={{ padding: '24px', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
                          {s.actualPeriod && (
                            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed var(--border-color)', paddingBottom: '8px' }}>
                              <span>Период начисления: <strong>{formatDate(s.actualPeriod.displayStart || s.actualPeriod.start)} — {formatDate(s.actualPeriod.end)}</strong></span>
                              {s.actualPeriod.payoutLabel && <span style={{ opacity: 0.8 }}>Ближайшая выплата: <strong>{s.actualPeriod.payoutLabel}</strong></span>}
                            </div>
                          )}
                          <AutoSalaryBreakdown
                            autoSalary={s}
                            filterEmployee={s.employeeId}
                            profile={profile}
                            filterPeriodStart={filterPeriodStart}
                            filterPeriodEnd={filterPeriodEnd}
                            setSalaryDetailModal={setSalaryDetailModal}
                            isAdmin={isAdmin}
                            isOwner={isOwner}
                            onPaySalary={s.paymentStatus === 'paid' ? undefined : () => {
                              handleOpenPayModal(s)
                              return true
                            }}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </React.Fragment>
              )
            })}
          </div>
        </div>

        <div style={{
          padding: '16px 14px 0',
          borderTop: '2px solid var(--surface-border)',
          marginTop: '12px'
        }}>
          {/* Блок А: За выбранный период */}
          <div style={{
            background: 'var(--surface-hover)',
            borderRadius: '10px',
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              За выбранный период {filterPeriodStart && filterPeriodEnd ? `(${formatDate(filterPeriodStart)} — ${formatDate(filterPeriodEnd)})` : ''}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', fontSize: '14px', fontWeight: 600 }}>
              <span style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                Начислено за выбранные даты:
              </span>
              <span style={{ color: 'var(--wb-primary)', fontSize: '15px' }}>{formatMoney(grandTotal)}</span>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                fontSize: '13px',
                color: totalPaidForPeriod > 0 ? 'var(--success)' : 'var(--text-secondary)',
                cursor: totalPaidForPeriod > 0 ? 'pointer' : 'default',
                transition: 'opacity 0.2s',
                flexWrap: 'wrap',
                gap: '6px'
              }}
              onClick={totalPaidForPeriod > 0 ? () => setShowPayoutsDetailModal(true) : undefined}
              title={totalPaidForPeriod > 0 ? "Показать подробности выплат" : undefined}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Выплаты, затрагивающие эти даты:
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: 600 }}>{formatMoney(totalPaidForPeriod)}</span>
                {totalPaidForPeriod > 0 && (
                  <span style={{ fontSize: '11px', textDecoration: 'underline', opacity: 0.85 }}>Подробнее</span>
                )}
              </span>
            </div>

            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: '1.35', marginTop: 'auto', borderTop: '1px dashed var(--border-color)', paddingTop: '6px' }}>
              * Некоторые выплаты включают зарплату за дни за пределами выбранного периода.
            </div>
          </div>
        </div>

        {avgSalaryData.total > 0 && avgSalaryData.days > 0 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 20px',
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.06), rgba(99, 102, 241, 0.04))',
            borderTop: '1px dashed var(--surface-border)',
            fontSize: '13px', color: 'var(--text-secondary)'
          }}>
            <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle',marginRight:'6px'}}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>Средняя ЗП в день <span style={{ opacity: 0.6 }}>({avgSalaryData.days} раб. дн.)</span></span>
            <span style={{ fontWeight: 600, color: 'var(--wb-primary)', fontSize: '14px' }}>{formatMoney(avgSalaryData.avg)}</span>
          </div>
        )}

        {/* Calendar of upcoming payouts */}
        {((extendedSalaries && extendedSalaries.length > 0) ? extendedSalaries : allSalaries).some(s => (s.actualDebt || 0) > 0 || (s.upcomingDebt || 0) > 0 || s.isReviewRequired || s.hasUnresolvedLegacy) && (
          <div style={{
            margin: '0',
            borderTop: '2px solid var(--surface-border)',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.03), rgba(139, 92, 246, 0.02))',
          }}>
            <div
              style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', justifyContent: 'space-between' }}
              onClick={() => setShowUpcomingPayouts(!showUpcomingPayouts)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>Ближайшие выплаты</span>
              </div>
              <span style={{ fontSize: '12px', opacity: 0.5, transform: showUpcomingPayouts ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'inline-flex' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg></span>
            </div>
            <div style={{ display: showUpcomingPayouts ? 'block' : 'none' }}>
              {(() => {
                const groups = {}
                const targetSalaries = (extendedSalaries && extendedSalaries.length > 0) ? extendedSalaries : allSalaries
                let totalUnfilteredDebt = 0
                targetSalaries.forEach(s => {
                  const isDataUnavailable = Boolean(s.financialDataUnavailable || s.status === 'data_unavailable')
                  const isRev = isDataUnavailable || Boolean(s.isReviewRequired || s.hasUnresolvedLegacy)
                  if (isRev) {
                    const payDate = s.actualPeriod?.payoutDate || s.actualPeriod?.end || 'review_required'
                    if (!groups[payDate]) groups[payDate] = { date: payDate, employees: [], total: 0, reviewCount: 0, reviewDebt: 0, unavailableCount: 0 }
                    groups[payDate].employees.push(s)
                    if (isDataUnavailable) {
                      groups[payDate].unavailableCount = (groups[payDate].unavailableCount || 0) + 1
                    } else {
                      groups[payDate].reviewCount = (groups[payDate].reviewCount || 0) + 1
                      const revAmount = Number(s.unresolvedDebt ?? s.actualDebt ?? 0)
                      groups[payDate].reviewDebt = (groups[payDate].reviewDebt || 0) + revAmount
                    }
                    return
                  }
                  const debt = s.upcomingDebt || 0
                  if (debt <= 0) return
                  totalUnfilteredDebt += debt
                  const payDate = s.actualPeriod?.payoutDate || s.actualPeriod?.end || null
                  if (!payDate) return
                  if (!groups[payDate]) groups[payDate] = { date: payDate, employees: [], total: 0, reviewCount: 0, reviewDebt: 0, unavailableCount: 0 }
                  groups[payDate].employees.push(s)
                  groups[payDate].total += debt
                })
                const sortedGroups = Object.values(groups).sort((a, b) => a.date.localeCompare(b.date))
                const grandDebt = sortedGroups.reduce((s, g) => s + g.total, 0)

                if (sortedGroups.length === 0) {
                  if (totalUnfilteredDebt > 0) {
                    return (
                      <div style={{ padding: '16px 20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                        Есть невыплаченная задолженность ({Math.round(totalUnfilteredDebt).toLocaleString('ru-RU')} ₽), но даты ближайших выплат не определены
                      </div>
                    )
                  }
                  return (
                    <div style={{ padding: '16px 20px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle',marginRight:'6px'}}><polyline points="20 6 9 17 4 12"/></svg> Все зарплаты за текущие периоды выплачены
                    </div>
                  )
                }

                return (
                  <div style={{ padding: '0 20px 16px' }}>
                    {sortedGroups.map((group, gi) => {
                      const dateObj = new Date(group.date + 'T00:00:00')
                      const isDateValid = !isNaN(dateObj.getTime())
                      const isToday = isDateValid && group.date === new Date().toISOString().split('T')[0]
                      const isPast = isDateValid && group.date < new Date().toISOString().split('T')[0]
                      const dayLabel = isDateValid
                        ? dateObj.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' })
                        : 'Требуется сверка'

                      return (
                        <div key={group.date} style={{
                          marginBottom: gi < sortedGroups.length - 1 ? '12px' : '0',
                          background: isToday ? 'rgba(239, 68, 68, 0.06)' : isPast ? 'rgba(239, 68, 68, 0.03)' : 'var(--surface)',
                          borderRadius: '10px',
                          border: isToday ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid var(--surface-border)',
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            padding: '10px 14px',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            borderBottom: '1px solid var(--surface-border)',
                            background: isToday ? 'rgba(239, 68, 68, 0.04)' : 'transparent',
                          }}>
                            <span style={{ fontWeight: 600, fontSize: '13px', color: isToday ? 'var(--danger)' : 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              {isToday && <span style={{display:'inline-flex'}}><svg width="10" height="10" viewBox="0 0 24 24" fill="var(--danger)" stroke="none"><circle cx="12" cy="12" r="10"/></svg></span>}
                              {isPast && <span style={{display:'inline-flex'}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span>}
                              {dayLabel}
                            </span>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                              <span style={{ fontWeight: 700, fontSize: '14px', color: isToday ? 'var(--danger)' : 'var(--wb-primary)', fontVariantNumeric: 'tabular-nums' }}>
                                {formatMoney(group.total)}
                              </span>
                              {group.unavailableCount > 0 && (
                                <span style={{
                                  fontSize: '11px',
                                  color: 'var(--danger, #dc2626)',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  fontWeight: 500
                                }}>
                                  Данные недоступны: {group.unavailableCount} {pluralize(group.unavailableCount, 'сотрудник', 'сотрудника', 'сотрудников')}
                                </span>
                              )}
                              {group.reviewCount > 0 && (
                                <span style={{
                                  fontSize: '11px',
                                  color: '#d97706',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  fontWeight: 500,
                                  fontVariantNumeric: 'tabular-nums'
                                }}>
                                  На сверке: {group.reviewCount} {pluralize(group.reviewCount, 'сотрудник', 'сотрудника', 'сотрудников')}{group.reviewDebt > 0 ? ` · ${formatMoney(group.reviewDebt)}` : ''} не включено в итог
                                </span>
                              )}
                            </div>
                          </div>
                          {group.employees.map((s, i) => (
                            <div key={s.employeeId} style={{
                              padding: '8px 14px',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              borderBottom: i < group.employees.length - 1 ? '1px solid var(--surface-border)' : 'none',
                              fontSize: '13px',
                            }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{s.employeeName}</span>
                                  {(s.isLegacy || s.is_legacy) && (
                                    <span style={{
                                      fontSize: '10px',
                                      padding: '1px 6px',
                                      borderRadius: '4px',
                                      background: 'var(--surface-subtle, rgba(255, 255, 255, 0.06))',
                                      color: 'var(--text-tertiary, #9ca3af)',
                                      border: '1px solid var(--surface-border, rgba(255, 255, 255, 0.1))',
                                      fontWeight: 500,
                                      flexShrink: 0
                                    }}>
                                      Уволен
                                    </span>
                                  )}
                                </div>
                                {s.actualPeriod && (
                                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                                    за {formatDate(s.actualPeriod.displayStart || s.actualPeriod.start)} — {formatDate(s.actualPeriod.end)}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {(() => {
                                  const isDataUnavailable = Boolean(s.financialDataUnavailable || s.status === 'data_unavailable')
                                  if (isDataUnavailable) {
                                    return (
                                      <span style={{
                                        fontSize: '11px',
                                        padding: '3px 8px',
                                        borderRadius: '6px',
                                        background: 'rgba(239, 68, 68, 0.12)',
                                        color: 'var(--danger, #dc2626)',
                                        fontWeight: 600,
                                        border: '1px solid rgba(239, 68, 68, 0.25)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }} title={s.unresolvedLegacyNotice || 'Не удалось загрузить все данные для расчёта зарплаты'}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                        Данные недоступны
                                      </span>
                                    )
                                  }
                                  const isRev = Boolean(s.isReviewRequired || s.hasUnresolvedLegacy)
                                  if (isRev) {
                                    return (
                                      <span style={{
                                        fontSize: '11px',
                                        padding: '3px 8px',
                                        borderRadius: '6px',
                                        background: 'rgba(245, 158, 11, 0.15)',
                                        color: '#d97706',
                                        fontWeight: 600,
                                        border: '1px solid rgba(245, 158, 11, 0.3)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }} title="Требуется сверка исторических выплат">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                        Требуется сверка
                                      </span>
                                    )
                                  }
                                  return (
                                    <>
                                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatMoney(s.upcomingDebt)}</span>
                                      {isOwner && (
                                        <button
                                          className="btn btn-primary"
                                          style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', whiteSpace: 'nowrap' }}
                                          onClick={async (e) => {
                                            e.stopPropagation()
                                            const periodLabel = s.actualPeriod ? `${formatDate(s.actualPeriod.displayStart || s.actualPeriod.start)} — ${formatDate(s.actualPeriod.end)}` : ''
                                            if (!window.confirm(`Выплатить ${s.employeeName}\nПериод: ${periodLabel}\nСумма: ${formatMoney(s.upcomingDebt)}`)) return
                                            try {
                                              await api.createPayment({
                                                employee_id: s.employeeId,
                                                type: 'salary',
                                                amount: s.upcomingDebt,
                                                period_start: s.actualPeriod?.start || filterPeriodStart,
                                                period_end: s.actualPeriod?.end || filterPeriodEnd,
                                                created_by: profile.id
                                              })
                                              toast.success(`Выплата ${s.employeeName}: ${formatMoney(s.upcomingDebt)}`)
                                              setRefreshTrigger(prev => prev + 1)
                                            } catch { toast.error('Ошибка выплаты') }
                                          }}
                                        >
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                                        </button>
                                      )}
                                    </>
                                  )
                                })()}
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    })}
                    {sortedGroups.length > 1 && (
                      <div style={{
                        marginTop: '12px', padding: '12px 14px',
                        background: 'linear-gradient(135deg, rgba(203, 17, 171, 0.08), rgba(147, 51, 234, 0.06))',
                        borderRadius: '10px',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}>
                        <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>Всего к выплате:</span>
                        <span style={{ fontWeight: 700, fontSize: '16px', color: 'var(--wb-primary)' }}>{formatMoney(grandDebt)}</span>
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
